import { h } from "preact";
import { registerActions } from "../actionRegistry";
import { toastSuccess, toastError } from "../ui/messages";
import { api } from "../api";
import { transfer_show } from "../globalState";
import { renderIsland } from "../ui/renderIsland";
import { TransfersPanel } from "./components/TransfersPanel";
import type { JobStatus, TransferHistoryEntry, TransferJobView } from "./components/TransfersPanel";

/**
 * The eCommerce transfer client — the browser half of what `transfer.php` used to do as a
 * daemon. `ecommerce.transfer` still only QUEUES jobs; this drives them to Shopify one at a
 * time via `ecommerce.transferOne`, exactly like the newsletter sendout drives `sendOne`.
 *
 * Three rules shape everything below:
 *   1. ONE client at a time per reseller. The store is shared, so we take a server lease
 *      (`transferClaim`) and renew it while running; a second tab sees "In use by <name>".
 *   2. A Shopify daily-variant refusal pauses the WHOLE run, not just that job, and the
 *      countdown lives in the DB (`delayed_at`) so it survives reload — reopening the page
 *      picks the same deadline back up, and a closed browser simply resumes on the next visit.
 *   3. A template error stops the run and stays on screen until the user fixes it and retries;
 *      grinding through the rest would just produce the same failure N more times.
 */

/** Lease renewal cadence. Must be well inside the server's 90s lease. */
const HEARTBEAT_MS = 30_000;
/** Poll cadence when idle (someone else may release the lock, or a pause may expire). */
const IDLE_POLL_MS = 15_000;

let heartbeatTimer: ReturnType<typeof setInterval> | undefined;
let tickTimer: ReturnType<typeof setInterval> | undefined;
let idleTimer: ReturnType<typeof setTimeout> | undefined;

let running = false;
let lockToken: string | null = null;
let jobs: TransferJobView[] = [];
let lockedBy: string | null = null;
let pausedUntil: number | null = null;
let failure: string | null = null;
let history: TransferHistoryEntry[] | null = null;
let historyLoading = false;
/** Set by Abort so an in-flight run stops at the next job instead of racing the deletion. */
let aborted = false;

/**
 * Guard against closing/reloading mid-run. The browser drives the loop, so navigating away
 * stops it: whatever has not been transferred stays QUEUED and the lock lapses on its heartbeat.
 * Nothing is corrupted — the next visit resumes — but the user should know they are stopping it.
 *
 * The message is NOT ours to choose: every current browser shows its own generic
 * "Leave site?" text and ignores any string we supply — which is why this only calls
 * `preventDefault()` and does not set the deprecated `returnValue`. Chrome additionally
 * requires the tab to have been interacted with, which a transfer always has been (the user
 * clicked Start Transfer).
 */
function beforeUnload(e: BeforeUnloadEvent): void {
  e.preventDefault();
}

function setUnloadGuard(active: boolean): void {
  window.removeEventListener("beforeunload", beforeUnload);
  if (active) window.addEventListener("beforeunload", beforeUnload);
}

function clearTimers(): void {
  clearInterval(heartbeatTimer);
  clearInterval(tickTimer);
  clearTimeout(idleTimer);
  heartbeatTimer = undefined;
  tickTimer = undefined;
  idleTimer = undefined;
}

function pausedSeconds(): number | null {
  if (pausedUntil === null) return null;
  return Math.max(0, Math.round((pausedUntil - Date.now()) / 1000));
}

function render(): void {
  renderIsland(
    "transfers-inner",
    h(TransfersPanel, {
      jobs,
      lockedBy,
      pausedSeconds: pausedSeconds(),
      running,
      failure,
      onAbort: () => void aborttransfers(),
      onRetry: () => void runTransfers(),
      history,
      historyLoading,
      onShowHistory: () => void showHistory(),
      onHideHistory: () => {
        history = null;
        render();
      },
    })
  );
}

function setStatus(jobId: string, status: JobStatus, error = ""): void {
  jobs = jobs.map((j) => (j.id === jobId ? { ...j, status, error } : j));
  render();
}

/** Attach what a finished job actually did, for the completed row's detail line. */
function setDetail(jobId: string, detail: NonNullable<TransferJobView["detail"]>): void {
  jobs = jobs.map((j) => (j.id === jobId ? { ...j, detail } : j));
  render();
}

/** Previous transfers for the whole reseller — the log behind the "Show history" button. */
export async function showHistory(): Promise<void> {
  historyLoading = true;
  render();
  try {
    const res = await api.ecommerce.transferHistory({ limit: 50 });
    history = res.entries;
  } catch (err: unknown) {
    toastError((err as Error).message);
    history = [];
  } finally {
    historyLoading = false;
    render();
  }
}

/** Keep the lease alive; if it is lost, stop rather than race the new holder. */
function startHeartbeat(): void {
  clearInterval(heartbeatTimer);
  heartbeatTimer = setInterval(() => {
    if (!lockToken) return;
    void api.ecommerce.transferHeartbeat({ token: lockToken }).catch(() => {
      running = false;
      lockToken = null;
      setUnloadGuard(false);
      clearTimers();
      lockedBy = "another user";
      render();
    });
  }, HEARTBEAT_MS);
}

/** While parked on the Shopify limit, tick the countdown and resume when it reaches zero. */
function startCountdown(): void {
  clearInterval(tickTimer);
  tickTimer = setInterval(() => {
    const left = pausedSeconds();
    render();
    if (left !== null && left <= 0) {
      clearInterval(tickTimer);
      tickTimer = undefined;
      pausedUntil = null;
      void runTransfers();
    }
  }, 1000);
}

async function releaseLock(): Promise<void> {
  const token = lockToken;
  lockToken = null;
  clearInterval(heartbeatTimer);
  heartbeatTimer = undefined;
  if (token) await api.ecommerce.transferRelease({ token }).catch(() => undefined);
}

/** Re-check later without holding the lock — another client may finish, or a pause expire. */
function scheduleIdlePoll(): void {
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => void runTransfers(), IDLE_POLL_MS);
}

export async function runTransfers(): Promise<void> {
  if (running || transfer_show !== 1) return;
  clearTimers();
  failure = null;
  history = null;
  aborted = false;

  let queue;
  try {
    queue = await api.ecommerce.transferQueue();
  } catch (err: unknown) {
    toastError((err as Error).message);
    scheduleIdlePoll();
    return;
  }

  jobs = queue.jobs;
  lockedBy = queue.lockedBy;
  pausedUntil = queue.pausedUntil ? new Date(queue.pausedUntil).getTime() : null;

  // Someone else is running: show who and try again later. Never start a second client.
  if (lockedBy) {
    render();
    scheduleIdlePoll();
    return;
  }
  // Parked on the Shopify limit — the deadline came from the DB, so this survives a reload.
  if (pausedSeconds() !== null) {
    render();
    startCountdown();
    return;
  }
  if (jobs.length === 0) {
    render();
    return;
  }

  const claim = await api.ecommerce.transferClaim().catch(() => null);
  if (!claim || !claim.ok) {
    lockedBy = claim?.ok === false ? claim.holderName : "another user";
    render();
    scheduleIdlePoll();
    return;
  }

  lockToken = claim.token;
  running = true;
  setUnloadGuard(true);
  startHeartbeat();
  render();

  try {
    for (const job of jobs) {
      if (aborted) break;
      if (!lockToken) break; // lease lost mid-run
      if (job.status === "COMPLETED") continue;
      setStatus(job.id, "TRANSFERRING");

      let result;
      try {
        result = await api.ecommerce.transferOne({ jobId: job.id, token: lockToken });
      } catch (err: unknown) {
        // A lost lease or a network blip — stop cleanly rather than half-transfer.
        failure = (err as Error).message;
        setStatus(job.id, "QUEUED");
        break;
      }

      if (result.outcome === "completed") {
        setStatus(job.id, "COMPLETED");
        setDetail(job.id, result.detail);
        continue;
      }
      if (result.outcome === "quota") {
        // Stop everything: the cap is per store, so the next job would fail the same way.
        setStatus(job.id, "QUOTA_EXCEEDED");
        const fresh = await api.ecommerce.transferQueue().catch(() => null);
        pausedUntil = fresh?.pausedUntil
          ? new Date(fresh.pausedUntil).getTime()
          : Date.now() + 86_400_000;
        break;
      }
      // Template error — surface it and stop; retrying the rest would repeat it.
      failure = result.message;
      setStatus(job.id, "FAILED", result.message);
      break;
    }
  } finally {
    running = false;
    setUnloadGuard(false);
    await releaseLock();
    render();
    if (pausedSeconds() !== null) startCountdown();
  }
}

export async function aborttransfers(): Promise<void> {
  if (!confirm("Are you sure you want to abort all transfers in queue?")) return;
  // Stop the loop first. Without this it would keep calling transferOne for jobs the abort
  // has just deleted, and each one would come back NOT_FOUND as a spurious "failure".
  aborted = true;
  await api.ecommerce.abortTransfers();
  toastSuccess("Transfers aborted");
  await runTransfers();
}

/** Kept for callers that only want the panel refreshed (menu item, post-queue kick). */
export async function refreshTransfers(): Promise<void> {
  await runTransfers();
}

registerActions({
  aborttransfers,
});
